import _ from 'lodash';
import React, {useState, useMemo, useCallback} from 'react';
import {useSnackbar} from 'notistack';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {Box, Card, IconButton, Skeleton, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography, useTheme} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import withMainContainer from '../components/WithMainContainer.js';
import api from '../util/api.js';
import {formatTime} from '../util/util.js';
import {closeModal, openModal} from '../components/Modals.js';
import SCButton from '../components/SCButton.js';
import {useBusyCallback} from '../util/react_hooks.js';
import {withTitle} from '../title.js';
import SCTextField from '../components/SCTextField.js';
import SCCheckbox from '../components/SCCheckbox.js';
import {withPermGuard} from '../components/PermGuard.js';
import {withLoginRequire} from '../components/Login.js';

const PERMISSIONS = {
    admin: 'מנהל',
    'tarako.host': 'טרקו',
    'tarotmail.student': 'אפליקציית המסרים',
};

const useAllUsers = ()=>{
    let {data, isLoading} = useQuery({
        queryKey: ['adminUsers'],
        queryFn: ()=>api.admin.users(),
    });
    return [data?.users||[], isLoading];
};

const PermissionsEditor = props=>{
    let {id, permissions} = props;
    let [editing, setEditing] = useState(permissions||[]);
    let {enqueueSnackbar} = useSnackbar();
    let queryClient = useQueryClient();
    let [submit, submitting] = useBusyCallback(async ()=>{
        try {
            await api.admin.updateUserPermissions(id, editing);
            enqueueSnackbar('ההרשאות נשמרו', {variant: 'success'});
            queryClient.invalidateQueries({queryKey: ['adminUsers']});
            closeModal();
        } catch (e) {            
            enqueueSnackbar(e.message, {variant: 'error'});
        }
    }, [editing, id, enqueueSnackbar, queryClient]);
    return <Stack direction="column" gap={2}>
      <Stack direction="column" gap={2} alignItems="start" sx={{maxHeight: '300px', overflowY: 'auto'}}>
        {_.keys(PERMISSIONS).filter(p=>p!='admin').map(p=><SCCheckbox key={p}
          value={editing.includes(p)} label={PERMISSIONS[p]}
          onChange={v=>setEditing(prev=>v ? [...prev, p] : prev.filter(k=>k!=p))}/>)}
      </Stack>
      <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
        <SCButton variant="contained" onClick={submit} loading={submitting} size="large">
          שמירה
        </SCButton>
        <SCButton text="ביטול" variant="text" onClick={closeModal}/>
      </Stack>
    </Stack>;
};

const searchFilter = search=>u=>u.username.toLowerCase().includes(search.toLowerCase())||
    u.email.toLowerCase().includes(search.toLowerCase());

const toReadablePermission = p=>PERMISSIONS[p]||p;

const AdminUsersSkeleton = ()=><Stack gap={3} direction="column" padding={3}>
  <Stack gap={3} direction="row" justifyContent="space-between" alignItems="center"
    flexWrap="wrap">
    <Typography variant="h3">ניהול משתמשים</Typography>
    <Skeleton variant="rounded" height={30} width={300}/>
  </Stack>
  <TableContainer component={Card}>
    <Table sx={{minWidth: 650}}>
      <TableHead sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
        <TableRow>
          <TableCell align="right">
            <Typography>שם</Typography>
          </TableCell>
          <TableCell align="right">
            <Typography>דוא"ל</Typography>
          </TableCell>
          <TableCell align="right">
            <Typography>הצטרף ב</Typography>
          </TableCell>
          <TableCell align="right">
            <Typography>הרשאות</Typography>
          </TableCell>
          <TableCell align="right">
            <Typography>פעולות</Typography>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Array(3).fill(null).map((__, i)=><TableRow key={i}>
          {Array(5).fill(null).map((__, i)=><TableCell key={i} align="right">
            <Skeleton variant="rounded" width={200} height={30}/>
          </TableCell>)}
        </TableRow>)}
      </TableBody>
    </Table>
  </TableContainer>
</Stack>;

const RefreshTokenModal = props=>{
    let {user} = props;
    let theme = useTheme();
    let {enqueueSnackbar} = useSnackbar();
    let [doRefresh, refreshing] = useBusyCallback(async ()=>{
        await api.admin.refreshToken(user.id);
        closeModal();
        enqueueSnackbar(`הטוקן של ${user.username} אופס בהצלחה`, {variant: 'success'});
    }, [user, enqueueSnackbar]);
    return <Stack direction="column" gap={2}>
      <Typography>
        הפעולה הזו תנתק את המשתמש מכל המכשירים (טוב אם יש חשד לפריצה לחשבון)
      </Typography>
      <Stack direction="row" gap={2} justifyContent="end" alignItems="end">
        <SCButton text="ביטול" variant="outlined" onClick={closeModal}/>
        <SCButton text="איפוס" variant="contained" onClick={doRefresh} loading={refreshing} color="error"/>
      </Stack>
    </Stack>;
};

const AdminUsers = ()=>{
    let [users, loading] = useAllUsers();
    let [search, setSearch] = useState('');
    let filteredUsers = useMemo(
        ()=>search?.length ? users.filter(searchFilter(search)) : users,
        [users, search]);
    let refreshToken = useCallback(user=>openModal({
        header: `לאפס טוקן עבור ${user.username}?`,
        content: <RefreshTokenModal user={user}/>
    }), []);
    if (loading)
        return <AdminUsersSkeleton/>;
    return <Stack gap={3} direction="column" padding={3}>
      <Stack gap={3} direction="row" justifyContent="space-between" alignItems="center"
        flexWrap="wrap">
        <Typography variant="h3">ניהול משתמשים</Typography>
        <Box sx={{width: '300px'}}>
          <SCTextField placeholder="חיפוש" size="small" variant="outlined" value={search} onChange={setSearch}/>
        </Box>
      </Stack>
      <TableContainer component={Card}>
        <Table sx={{minWidth: 650}}>
          <TableHead sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
            <TableRow>
              <TableCell align="right">
                <Typography>שם</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>דוא"ל</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>הצטרף ב</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>הרשאות</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>פעולות</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map(u=><TableRow key={u.id}>
              <TableCell align="right">{u.username}</TableCell>
              <TableCell align="right">{u.email}</TableCell>
              <TableCell align="right">{formatTime(u.created)}</TableCell>
              <TableCell align="right">
                <Stack direction="row" gap={1} alignItems="center" width="100%" justifyContent="space-between">
                  <Typography>
                    {u.permissions?.length ? u.permissions.map(toReadablePermission).join(', ') : 'אין הרשאות'}
                  </Typography>
                  {!u.permissions?.includes('admin')&&<IconButton size="small" onClick={()=>openModal({
                      header: `ניהול הרשאות עבור ${u.username}`,
                      content: <PermissionsEditor id={u.id} permissions={u.permissions}/>,
                  })}>
                    <EditIcon/>
                  </IconButton>}
                </Stack>
              </TableCell>
              <TableCell align="right">
                <SCButton text="איפוס טוקן" variant="outlined" size="small" onClick={()=>refreshToken(u)}/>
              </TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>;
};

export default withTitle('ניהול משתמשים', withMainContainer(withLoginRequire(withPermGuard(AdminUsers, 'admin'))));
